import { db } from "helpers/firebase/firebaseConfig";
import { collection, addDoc, getDoc, doc, updateDoc, query, where, getDocs } from "firebase/firestore";

// Handles Firestore Timestamp, ISO string, or Date object
export const getBookingExecuteDate = (bookingExecuteDate) => {
  if (!bookingExecuteDate) return null;
  if (bookingExecuteDate?.seconds) return new Date(bookingExecuteDate.seconds * 1000);
  if (typeof bookingExecuteDate === "string") {
    const parsed = new Date(bookingExecuteDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(bookingExecuteDate);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Returns true if the order is currently delayed
export const isOrderDelayed = (order) => {
  const bookingStatus = String(order?.booking_status || "").toLowerCase();
  const paymentStatus = String(order?.paymentStatus || "").toLowerCase();
  const executeDate = getBookingExecuteDate(order?.booking_execute_date);
  if (!executeDate) return false;
  const eligibleStatus = bookingStatus === "pending" || bookingStatus === "approved";
  const isPaid = paymentStatus === "paid";
  return eligibleStatus && isPaid && executeDate.getTime() < Date.now();
};

/**
 * Calculate cancellation fees based on time remaining until booking execution
 * @param {Date|string} bookingExecuteDate - The scheduled execution date/time of the booking
 * @param {Number} bookingAmount - The original booking amount in Riyal
 * @returns {Object} - { baseFine, percentageFee, totalFine, reason }
 */
export const calculateCancellationFee = (bookingExecuteDate, bookingAmount) => {
  // Ensure we have a valid execution date; default to 12 hours from now (treated as late cancellation)
  const validExecuteDate = bookingExecuteDate || new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString();

  // Handle Firestore Timestamp objects
  let executeDate;
  if (validExecuteDate && typeof validExecuteDate === 'object' && validExecuteDate.seconds) {
    // Firestore Timestamp object
    executeDate = new Date(validExecuteDate.seconds * 1000);
  } else if (validExecuteDate && typeof validExecuteDate === 'object' && validExecuteDate.toDate) {
    // Firestore Timestamp with toDate method
    executeDate = validExecuteDate.toDate();
  } else {
    // String or other format
    executeDate = new Date(validExecuteDate);
  }

  const cancellationDate = new Date();

  // Handle invalid dates
  if (isNaN(executeDate.getTime())) {
    console.warn("Invalid booking execute date:", bookingExecuteDate);
    // Default to 12 hours from now (early cancellation)
    executeDate = new Date(Date.now() + 12 * 60 * 60 * 1000);
  }

  // Hours remaining until execution at the time of cancellation
  const hoursUntilExecution = (executeDate - cancellationDate) / (1000 * 60 * 60);

  // Calculate percentage fee (2.4%)
  const percentageFee = (bookingAmount * 2.4) / 100;

  let baseFine = 0;
  let reason = "";

  if (hoursUntilExecution < 24) {
    // إلغاء متأخر: الإلغاء قريب من موعد التنفيذ (أقل من 24 ساعة) - 20 ريال غرامة + 2.4% + 2 ريال
    baseFine = 20;
    reason = "إلغاء متأخر: الإلغاء قريب من موعد التنفيذ (أقل من 24 ساعة متبقية) - 20 ريال غرامة إضافية بالإضافة لرسوم السداد الإلكتروني (2.4% + 2 ريال)";
  } else {
    // إلغاء مبكر: الإلغاء بعيد عن موعد التنفيذ (24 ساعة أو أكثر) - فقط 2.4% + 2 ريال
    baseFine = 0;
    reason = "إلغاء مبكر: الإلغاء بعيد عن موعد التنفيذ (24 ساعة أو أكثر متبقية) - رسوم السداد الإلكتروني فقط (2.4% + 2 ريال)";
  }

  // Add fixed 2 Riyal to all cases
  const fixedFee = 2;
  const totalFine = baseFine + percentageFee + fixedFee;

  return {
    baseFine,
    percentageFee: parseFloat(percentageFee.toFixed(2)),
    fixedFee,
    totalFine: parseFloat(totalFine.toFixed(2)),
    hoursUntilExecution: parseFloat(Math.max(0, hoursUntilExecution).toFixed(2)),
    reason,
  };
};

/**
 * Create a cancellation tracking record and generate invoice
 * @param {Object} orderData - The order/booking data
 * @param {string} serviceProviderId - The service provider ID
 * @param {string} cancellationReason - Reason for cancellation
 * @returns {Object} - Created cancellation tracking record
 */
export const createCancellationInvoice = async (
  orderData,
  serviceProviderId,
  cancellationReason = "مقدم الخدمة طلب الإلغاء"
) => {
  try {
    // 1. Validate input data
    if (!orderData || !orderData.id || !serviceProviderId) {
      throw new Error("Invalid order or service provider data");
    }

    // Get service provider data for company details
    const providerRef = doc(db, "service_providers", serviceProviderId);
    const providerSnap = await getDoc(providerRef);

    if (!providerSnap.exists()) {
      throw new Error("Service provider not found");
    }

    const providerData = providerSnap.data() || {};

    // 2. Get booking execution date (the scheduled time the service will be performed)
    const bookingExecuteDate = orderData?.booking_execute_date || null;

    // 3. Get service price (prioritize total_after_discount from order data)
    const bookingAmount = orderData?.total_after_discount || orderData?.service_price || orderData?.total_price || orderData?.amount || 0;

    // 4. Calculate cancellation fees based on time remaining until execution
    const feeCalculation = calculateCancellationFee(
      bookingExecuteDate,
      bookingAmount
    );


    // 5. Create cancellation tracking record
    const cancellationTrackData = {
      order_id: orderData.id,
      service_provider_id: serviceProviderId,
      service_provider_name: providerData?.full_name || providerData?.full_name || "Unknown Provider",
      service_provider_company: providerData?.salon_name || providerData?.salon_name || "N/A",
      service_provider_email: providerData?.email || "N/A",
      service_provider_phone: providerData?.mobile || providerData?.phone || "N/A",
      
      // Store service information
      serviceProviderServices: orderData?.serviceProviderServices || null,
      service_name: orderData?.service_name || "N/A",
      service_price: bookingAmount,
      
      booking_execute_date: bookingExecuteDate,
      cancellation_date: new Date().toISOString(),
      cancellation_reason: cancellationReason,
      booking_amount: bookingAmount,
      
      // Fee breakdown with clear description
      base_fine: feeCalculation.baseFine,
      percentage_fee: feeCalculation.percentageFee,
      fixed_fee: feeCalculation.fixedFee,
      total_fine: feeCalculation.totalFine,
      fee_description: `${feeCalculation.percentageFee} ريال سعودي (2.4%) + 2 ريال سعودي${feeCalculation.baseFine > 0 ? ` + ${feeCalculation.baseFine} ريال سعودي (غرامة إلغاء متأخر)` : ''}`,
      
      // Metadata
      hours_until_execution: feeCalculation.hoursUntilExecution,
      cancellation_type: feeCalculation.hoursUntilExecution < 24 ? "late_cancellation" : "early_cancellation",
      fee_reason: feeCalculation.reason,
      status: "pending", // Can be: pending, confirmed, disputed
      deduction_status: "pending", // Can be: pending, deducted, failed
      deducted_at: null, // Timestamp when amount was deducted
      deducted_by: null, // Admin ID who deducted the amount
      notes: `رسوم الإلغاء محسوبة كالتالي: 2.4% من مبلغ الحجز (${bookingAmount} ريال سعودي) + 2 ريال سعودي كرسوم ثابتة${feeCalculation.baseFine > 0 ? ` + ${feeCalculation.baseFine} ريال سعودي كغرامة إلغاء متأخر` : ''}`,
      
      // Additional tracking
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // 4. Add to cancellation_track collection
    const cancellationTrackRef = collection(db, "cancellation_track");
    const cancellationDocSnap = await addDoc(cancellationTrackRef, cancellationTrackData);

    // 5. Return success response
    return {
      success: true,
      cancellationTrackId: cancellationDocSnap.id,
      cancellationTrackData,
      feeCalculation,
      message: `Cancellation track record created successfully. Total fine: ${feeCalculation.totalFine} SAR`,
    };
  } catch (error) {
    console.error("Error creating cancellation invoice:", error);
    return {
      success: false,
      error: error.message,
      message: `Failed to create cancellation invoice: ${error.message}`,
    };
  }
};

/**
 * Get cancellation history for a service provider
 * @param {string} serviceProviderId - Service provider ID
 * @returns {Array} - Array of cancellation records
 */
export const getCancellationHistory = async (serviceProviderId) => {
  try {
    const cancellationTrackRef = collection(db, "cancellation_track");
    const q = query(
      cancellationTrackRef,
      where("service_provider_id", "==", serviceProviderId)
    );
    const querySnapshot = await getDocs(q);

    const records = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return records;
  } catch (error) {
    console.error("Error fetching cancellation history:", error);
    throw error;
  }
};

/**
 * Get cancellation statistics for a service provider
 * @param {string} serviceProviderId - Service provider ID
 * @returns {Object} - Statistics object
 */
export const getCancellationStatistics = async (serviceProviderId) => {
  try {
    const cancellations = await getCancellationHistory(serviceProviderId);

    const stats = {
      total_cancellations: cancellations.length,
      early_cancellations: cancellations.filter((c) => c.cancellation_type === "early_cancellation").length,
      late_cancellations: cancellations.filter((c) => c.cancellation_type === "late_cancellation").length,
      total_fines: cancellations.reduce((sum, c) => sum + (c.total_fine || 0), 0),
      pending_invoices: cancellations.filter((c) => c.status === "pending").length,
    };

    return stats;
  } catch (error) {
    console.error("Error calculating cancellation statistics:", error);
    throw error;
  }
};
